/**
 * Pipedrive CRM Integration
 * Implements comprehensive Pipedrive integration with API token authentication,
 * person and deal synchronization, and email engagement tracking
 */

import { BaseCRMIntegration, CRMContact, CRMActivity, CRMSyncOptions, CRMSyncResult } from '../base/BaseCRMIntegration';
import { 
  IntegrationConfig, 
  FieldMapping,
  ValidationResult,
  TestResult,
  ConnectionResult,
  SyncOperation
} from '../../../types/integration';
import { APIGateway } from '../../api/APIGateway';

export interface PipedriveConfig extends IntegrationConfig {
  apiToken: string;
  companyDomain: string; // e.g., 'mycompany' for mycompany.pipedrive.com
}

export interface PipedrivePerson {
  id?: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: Array<{ value: string; primary: boolean; label: string }>;
  phone?: Array<{ value: string; primary: boolean; label: string }>;
  org_id?: number;
  owner_id?: number;
  add_time?: string;
  update_time?: string;
  visible_to?: string;
  custom_fields?: Record<string, any>;
  [key: string]: any;
}

export interface PipedriveDeal {
  id?: number;
  title?: string;
  value?: number;
  currency?: string;
  user_id?: number;
  person_id?: number;
  org_id?: number;
  stage_id?: number;
  status?: string;
  add_time?: string;
  update_time?: string;
  [key: string]: any;
}

export interface PipedriveWebhookPayload {
  v: number;
  matches_filters: {
    current: any[];
    previous: any[];
  };
  event: string;
  object: string;
  timestamp: string;
  subscriptions_urls: string[];
  current: PipedrivePerson | PipedriveDeal;
  previous?: PipedrivePerson | PipedriveDeal;
}

export class PipedriveIntegration extends BaseCRMIntegration {
  private apiGateway: APIGateway;
  private baseUrl: string;
  protected config: PipedriveConfig;

  constructor(config: PipedriveConfig) {
    super(config);
    this.config = config;
    this.baseUrl = `https://${config.companyDomain}.pipedrive.com/api/v1`;
    this.apiGateway = APIGateway.getInstance({
      defaultTimeout: 30000,
      maxRetryAttempts: 3,
      rateLimitConfig: {
        windowMs: 60000,
        maxRequests: 600
      },
      enableLogging: true,
      enableMetrics: true
    });
  }

  async connect(): Promise<ConnectionResult> {
    try {
      // Test connection by fetching user info
      const response = await this.apiGateway.get('/users/me', {
        params: { api_token: this.config.apiToken }
      });

      if (response.status >= 200 && response.status < 300 && response.data) {
        await this.emitEvent('connection.established', { 
          userId: response.data.id,
          userName: response.data.name 
        });
        
        return {
          success: true,
          message: 'Successfully connected to Pipedrive',
          connectionId: `pipedrive_${response.data.id}`,
          metadata: {
            userId: response.data.id,
            userName: response.data.name,
            companyName: response.data.company_name
          }
        };
      } else {
        throw new Error('Invalid API response');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown connection error';
      await this.emitEvent('connection.failed', { error: errorMessage });
      
      return {
        success: false,
        message: `Failed to connect to Pipedrive: ${errorMessage}`,
        error: errorMessage
      };
    }
  }

  async disconnect(): Promise<void> {
    // Pipedrive doesn't require explicit disconnection for API tokens
    await this.emitEvent('connection.disconnected', {});
  }

  async validateConfig(): Promise<ValidationResult> {
    const errors: any[] = [];
    const warnings: any[] = [];

    // Required fields validation
    if (!this.config.apiToken || this.config.apiToken.trim() === '') {
      errors.push({
        field: 'apiToken',
        message: 'API token is required',
        code: 'REQUIRED_FIELD_MISSING'
      });
    }

    if (!this.config.companyDomain || this.config.companyDomain.trim() === '') {
      errors.push({
        field: 'companyDomain',
        message: 'Company domain is required',
        code: 'REQUIRED_FIELD_MISSING'
      });
    }

    // API token format validation (basic check)
    if (this.config.apiToken && !this.config.apiToken.match(/^[a-f0-9]{40}$/)) {
      errors.push({
        field: 'apiToken',
        message: 'API token appears to be invalid format (should be 40 character hex string)',
        code: 'INVALID_FORMAT'
      });
    }

    // Company domain format validation
    if (this.config.companyDomain && !this.config.companyDomain.match(/^[a-zA-Z0-9-]+$/)) {
      errors.push({
        field: 'companyDomain',
        message: 'Company domain should only contain letters, numbers, and hyphens',
        code: 'INVALID_FORMAT'
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    try {
      const connectionResult = await this.connect();
      
      if (connectionResult.success) {
        // Additional tests
        const testsResults = await Promise.allSettled([
          this.testPersonsAccess(),
          this.testDealsAccess(),
          this.testCustomFieldsAccess()
        ]);

        const failedTests = testsResults
          .map((result, index) => ({ result, test: ['persons', 'deals', 'custom_fields'][index] }))
          .filter(({ result }) => result.status === 'rejected')
          .map(({ test, result }) => ({ 
            test, 
            error: result.status === 'rejected' ? result.reason : 'Unknown error' 
          }));

        return {
          success: failedTests.length === 0,
          message: failedTests.length === 0 
            ? 'All Pipedrive integration tests passed'
            : `${failedTests.length} tests failed`,
          duration: Date.now() - startTime,
          details: {
            connection: connectionResult,
            failedTests
          }
        };
      } else {
        return {
          success: false,
          message: 'Connection test failed',
          duration: Date.now() - startTime,
          details: { connectionResult }
        };
      }
    } catch (error) {
      return {
        success: false,
        message: `Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        duration: Date.now() - startTime,
        details: { error }
      };
    }
  }

  async syncContacts(options: CRMSyncOptions): Promise<CRMSyncResult> {
    const operation: SyncOperation = {
      id: `pipedrive_sync_${Date.now()}`,
      integrationId: this.config.id,
      type: 'incremental_sync',
      direction: options.direction,
      status: 'running',
      startedAt: new Date(),
      recordsProcessed: 0,
      recordsSucceeded: 0,
      recordsFailed: 0,
      errors: [],
      conflicts: [],
      metadata: {
        triggeredBy: 'manual',
        tags: ['pipedrive', 'crm'],
        customData: { platform: 'pipedrive', options }
      }
    };

    try {
      let contactsProcessed = 0;
      let contactsSucceeded = 0;
      let contactsFailed = 0;
      const errors: any[] = [];
      const conflicts: any[] = [];

      if (options.direction === 'from_platform' || options.direction === 'bidirectional') {
        // Sync from Pipedrive to email platform
        const persons = await this.getAllPersons(options.dateRange);
        
        for (const person of persons) {
          try {
            contactsProcessed++;
            const crmContact = this.transformPipedrivePersonToCRMContact(person);
            await this.syncContactFromCRM(crmContact.id);
            contactsSucceeded++;
          } catch (error) {
            contactsFailed++;
            errors.push({
              contactId: person.id,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }
      }

      if (options.direction === 'to_platform' || options.direction === 'bidirectional') {
        // Sync from email platform to Pipedrive would be implemented here
        // This would require access to the email platform's contact API
      }

      operation.status = 'completed';
      operation.completedAt = new Date();
      operation.recordsProcessed = contactsProcessed;
      operation.recordsSucceeded = contactsSucceeded;
      operation.recordsFailed = contactsFailed;
      operation.errors = errors;
      operation.conflicts = conflicts;

      await this.emitEvent('sync.completed', { operation });

      return {
        operation,
        contactsProcessed,
        contactsSucceeded,
        contactsFailed,
        activitiesProcessed: 0,
        activitiesSucceeded: 0,
        activitiesFailed: 0,
        conflicts,
        errors
      };
    } catch (error) {
      operation.status = 'failed';
      operation.completedAt = new Date();
      operation.errors = [{
        id: `error_${Date.now()}`,
        errorType: 'sync_error',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date(),
        retryable: true,
        retryCount: 0
      }];

      await this.emitEvent('sync.failed', { operation, error });
      throw error;
    }
  }

  async syncActivities(activities: CRMActivity[]): Promise<void> {
    // Pipedrive doesn't have a direct email activity API like HubSpot
    // We'll create notes on the person record for email activities
    for (const activity of activities) {
      try {
        await this.createPersonNote(activity.contactId, {
          content: this.formatActivityAsNote(activity),
          deal_id: null,
          person_id: parseInt(activity.contactId),
          org_id: null
        });
      } catch (error) {
        console.error(`Failed to sync activity ${activity.id}:`, error);
        // Continue with other activities
      }
    }
  }

  async getContact(contactId: string): Promise<CRMContact | null> {
    try {
      const response = await this.apiGateway.get(`/persons/${contactId}`, {
        params: { api_token: this.config.apiToken }
      });

      if (response.status >= 200 && response.status < 300 && response.data) {
        return this.transformPipedrivePersonToCRMContact(response.data);
      }
      return null;
    } catch (error) {
      console.error(`Failed to get contact ${contactId}:`, error);
      return null;
    }
  }

  async createContact(contact: Partial<CRMContact>): Promise<CRMContact> {
    const pipedriveData = this.transformCRMContactToPipedrivePerson(contact);
    
    const response = await this.apiGateway.post('/persons', pipedriveData, {
      params: { api_token: this.config.apiToken }
    });

    if (response.status >= 200 && response.status < 300 && response.data) {
      return this.transformPipedrivePersonToCRMContact(response.data);
    } else {
      throw new Error('Failed to create contact in Pipedrive');
    }
  }

  async updateContact(contactId: string, updates: Partial<CRMContact>): Promise<CRMContact> {
    const pipedriveData = this.transformCRMContactToPipedrivePerson(updates);
    
    const response = await this.apiGateway.put(`/persons/${contactId}`, pipedriveData, {
      params: { api_token: this.config.apiToken }
    });

    if (response.status >= 200 && response.status < 300 && response.data) {
      return this.transformPipedrivePersonToCRMContact(response.data);
    } else {
      throw new Error('Failed to update contact in Pipedrive');
    }
  }

  async deleteContact(contactId: string): Promise<void> {
    const response = await this.apiGateway.delete(`/persons/${contactId}`, {
      params: { api_token: this.config.apiToken }
    });

    if (response.status < 200 || response.status >= 300) {
      throw new Error('Failed to delete contact from Pipedrive');
    }
  }

  async searchContacts(query: string, limit: number = 10): Promise<CRMContact[]> {
    const response = await this.apiGateway.get('/persons/search', {
      params: { 
        term: query,
        limit,
        api_token: this.config.apiToken 
      }
    });

    if (response.status >= 200 && response.status < 300 && response.data?.items) {
      return response.data.items.map((item: any) => 
        this.transformPipedrivePersonToCRMContact(item.item)
      );
    }
    return [];
  }

  async getCustomFields(): Promise<FieldMapping[]> {
    const response = await this.apiGateway.get('/personFields', {
      params: { api_token: this.config.apiToken }
    });

    if (response.status >= 200 && response.status < 300 && response.data) {
      return response.data.map((field: any) => ({
        sourceField: field.key,
        targetField: field.key,
        direction: 'bidirectional' as const,
        required: field.mandatory_flag,
        dataType: this.mapPipedriveFieldType(field.field_type)
      }));
    }
    return [];
  }

  // Protected methods for webhook handling
  protected extractWebhookEventType(payload: PipedriveWebhookPayload): string {
    return `${payload.object}.${payload.event}`;
  }

  protected async handleContactWebhook(payload: PipedriveWebhookPayload): Promise<void> {
    if (payload.object === 'person') {
      const person = payload.current as PipedrivePerson;
      const crmContact = this.transformPipedrivePersonToCRMContact(person);
      
      // Sync to email platform
      await this.updateEmailPlatformContact(crmContact);
    }
  }

  protected async handleContactDeletedWebhook(payload: PipedriveWebhookPayload): Promise<void> {
    if (payload.object === 'person' && payload.event === 'deleted') {
      const person = payload.previous as PipedrivePerson;
      // Handle contact deletion in email platform
      await this.deleteEmailPlatformContact(person.id?.toString() || '');
    }
  }

  protected async updateEmailPlatformContact(contact: any): Promise<any> {
    // This would integrate with the email platform's API
    // For now, we'll emit an event that can be handled by the email platform service
    await this.emitEvent('contact.update_email_platform', { contact });
    return contact;
  }

  // Private helper methods
  private async testPersonsAccess(): Promise<void> {
    await this.apiGateway.get('/persons', {
      params: { 
        limit: 1,
        api_token: this.config.apiToken 
      }
    });
  }

  private async testDealsAccess(): Promise<void> {
    await this.apiGateway.get('/deals', {
      params: { 
        limit: 1,
        api_token: this.config.apiToken 
      }
    });
  }

  private async testCustomFieldsAccess(): Promise<void> {
    await this.apiGateway.get('/personFields', {
      params: { api_token: this.config.apiToken }
    });
  }

  private async getAllPersons(dateRange?: { start: Date; end: Date }): Promise<PipedrivePerson[]> {
    const persons: PipedrivePerson[] = [];
    let start = 0;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const params: any = {
        start,
        limit,
        api_token: this.config.apiToken
      };

      if (dateRange) {
        params.filter_id = await this.createDateRangeFilter(dateRange);
      }

      const response = await this.apiGateway.get('/persons', { params });

      if (response.status >= 200 && response.status < 300 && response.data) {
        persons.push(...response.data);
        hasMore = response.additional_data?.pagination?.more_items_in_collection || false;
        start += limit;
      } else {
        hasMore = false;
      }
    }

    return persons;
  }

  private async createDateRangeFilter(_dateRange: { start: Date; end: Date }): Promise<number> {
    // This would create a filter in Pipedrive for the date range
    // For simplicity, we'll return a default filter ID
    return 1;
  }

  private async createPersonNote(contactId: string, note: any): Promise<void> {
    await this.apiGateway.post('/notes', note, {
      params: { api_token: this.config.apiToken }
    });
  }

  private formatActivityAsNote(activity: CRMActivity): string {
    const timestamp = activity.timestamp.toISOString();
    const type = activity.type.replace('_', ' ').toUpperCase();
    
    let content = `[EMAIL ACTIVITY] ${type} - ${timestamp}`;
    
    if (activity.campaignId) {
      content += `\nCampaign: ${activity.campaignId}`;
    }
    
    if (activity.emailId) {
      content += `\nEmail: ${activity.emailId}`;
    }
    
    if (activity.details && Object.keys(activity.details).length > 0) {
      content += `\nDetails: ${JSON.stringify(activity.details, null, 2)}`;
    }
    
    return content;
  }

  private transformPipedrivePersonToCRMContact(person: PipedrivePerson): CRMContact {
    const primaryEmail = person.email?.find(e => e.primary)?.value || person.email?.[0]?.value || '';
    const primaryPhone = person.phone?.find(p => p.primary)?.value || person.phone?.[0]?.value || '';

    return {
      id: person.id?.toString() || '',
      email: primaryEmail,
      firstName: person.first_name,
      lastName: person.last_name,
      company: person.org_id?.toString(),
      phone: primaryPhone,
      customFields: person.custom_fields || {},
      createdAt: person.add_time ? new Date(person.add_time) : new Date(),
      updatedAt: person.update_time ? new Date(person.update_time) : new Date(),
      lastActivityAt: person.last_activity_date ? new Date(person.last_activity_date) : undefined
    };
  }

  private transformCRMContactToPipedrivePerson(contact: Partial<CRMContact>): Partial<PipedrivePerson> {
    const person: Partial<PipedrivePerson> = {};

    if (contact.firstName || contact.lastName) {
      person.name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
      person.first_name = contact.firstName;
      person.last_name = contact.lastName;
    }

    if (contact.email) {
      person.email = [{ value: contact.email, primary: true, label: 'work' }];
    }

    if (contact.phone) {
      person.phone = [{ value: contact.phone, primary: true, label: 'work' }];
    }

    if (contact.customFields) {
      person.custom_fields = contact.customFields;
    }

    return person;
  }

  private mapPipedriveFieldType(fieldType: string): 'string' | 'number' | 'date' | 'boolean' | 'object' {
    switch (fieldType) {
      case 'varchar':
      case 'text':
      case 'varchar_auto':
        return 'string';
      case 'double':
      case 'int':
        return 'number';
      case 'date':
      case 'datetime':
        return 'date';
      case 'enum':
        return 'string';
      case 'set':
        return 'object';
      default:
        return 'string';
    }
  }

  private async deleteEmailPlatformContact(contactId: string): Promise<void> {
    // This would integrate with the email platform's API to delete the contact
    await this.emitEvent('contact.delete_email_platform', { contactId });
  }
}